<template>
  <Popover
    ref="appGridRef"
    class="app-grid"
    @hide="onHide"
    @show="onShow"
  >
    <div v-focustrap v-if="!showFeedbackForm">
      <div class="section shortcuts">
        <header>
          <IconButton
            :icon="BackArrowLight"
            :icon-dark="BackArrowDark"
            class="back-arrow"
            @click="clickBack"
            aria-label="Back"
          />
          Shortcuts
        </header>
        
        <div class="apps">
          <button 
            autofocus
            @click="goToURL(LINKS.MESSENGER_PIGEON)"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                goToURL(LINKS.MESSENGER_PIGEON);
              }
            }"  
            :aria-label="'Messenger Pigeon'"
          >
            <img role="presentation" :src="MessengerPigeonLogo" alt="Messenger Pigeon" />
            <span>Messenger Pigeon</span>
          </button>
          <button 
            @click="goToURL(LINKS.CORAL)"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                goToURL(LINKS.CORAL);
              }
            }"
            :aria-label="'Coral'"  
          >
            <img role="presentation" :src="CoralLogo" alt="Coral" />
            <span>Coral</span>
          </button>
          <button
            @click="goToURL(LINKS.BEEHIVE)"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                goToURL(LINKS.BEEHIVE);
              }
            }"
            :aria-label="'Beehive'"
          >
            <img role="presentation" :src="BeehiveLogo" alt="Beehive" />
            <span>Beehive</span>
          </button>
          <button 
            @click="goToURL(LINKS.HABITAT_LEARN)"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                goToURL(LINKS.HABITAT_LEARN);
              }
            }"
            :aria-label="'Habitat Learn'"  
          >
            <img role="presentation" :src="HabitatLearnLogo" alt="Habitat Learn" />
            <span>Habitat Learn</span>
          </button>
        </div>
      </div>
  
      <div class="section help-feedback">
        <header>Help & Feedback</header>
        <div class="links">
          <TextButton
            text="Products & Services"
            @click="goToURL(LINKS.HABITAT_LEARN_PRODUCTS)"
          />
          <TextButton
            text="Help Center"
            @click="goToURL(LINKS.HABITAT_LEARN_CONTACT)"
          />
          <TextButton
            text="Submit Feedback"
            @click="openFeedbackForm"
          />
        </div>
      </div>
    </div>
    <Transition name="slide-up">
      <FeedbackForm
        v-if="showFeedbackForm"
        @close="closeFeedbackForm"
        @clear="clearFeedback"
        :size="appGridSize"
      />
    </Transition>
  </Popover>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick } from 'vue'
import CoralLogo from "../assets/images/logos/Coral-Logo-Light.svg"
import BeehiveLogo from "../assets/images/logos/Beehive-Logo-Light.svg"
import HabitatLearnLogo from "../assets/images/logos/Habitat-Learn-Logo-Light.svg"
import MessengerPigeonLogo from "../assets/images/logos/MP-Logo-Light.svg"
import TextButton from "./TextButton.vue"
import IconButton from "../components/IconButton.vue"
import BackArrowLight from "../assets/images/Back-Arrow-Light.svg"
import BackArrowDark from "../assets/images/Back-Arrow-Dark.svg"
import { LINKS } from "../assets/constants"
import FeedbackForm from "../components/FeedbackForm.vue"

interface AppGridSize {
  width: string
  height: string
}

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()
const appGridRef = ref()

// Reactive data
const showFeedbackForm = ref<boolean>(false)
const appGridSize = reactive<AppGridSize>({
  width: '',
  height: ''
})

// Methods
const clickBack = (): void => {
  if (appGridRef.value) {
    appGridRef.value.hide()
  }
  emit('update:visible', false)
}

const onHide = (): void => {
  clearFeedback()
  emit('update:visible', false)
}

const onShow = (): void => {
  getSize()
  emit('update:visible', true)
}

const goToURL = (url: string): void => {
  window.location.href = url
}

const openFeedbackForm = (): void => {
  showFeedbackForm.value = true
}

const closeFeedbackForm = (): void => {
  showFeedbackForm.value = false
}

const clearFeedback = (): void => {
  showFeedbackForm.value = false
}

// Method to be called by parent to toggle the popover
const toggle = (event: Event): void => {
  if (appGridRef.value) {
    appGridRef.value.toggle(event)
  }
}

const getSize = (): void => {
  nextTick(() => {
    const el = document.querySelector('.p-popover-content') as HTMLElement
    if (el) {
      appGridSize.width = el.clientWidth + 'px'
      appGridSize.height = el.clientHeight + 'px'
    }
  })
}

// Expose the toggle method for parent component
defineExpose({
  toggle
})
</script>

<style lang="scss">
$icon-size-s: 4rem;
$icon-size-m: 4.8rem;
$icon-size-l: 5.6rem;
$button-padding: 2.4rem;
$outside-horizontal-padding-s: 2rem;
$outside-horizontal-padding-l: 3.2rem;
$screen-width-breakpoint: 500;
$screen-height-breakpoint: 700;

.app-grid {
  background-color: map-get($colours, accent-purple-2);
  --p-popover-border-radius: 1.6rem;
  width: 100%;
  min-height: 100%;
  margin-top: 0;

  @include cwh($screen-width-breakpoint, $screen-height-breakpoint) {
    width: initial;
    min-height: initial;
    margin-top: initial;
    border-radius: 1.4rem;

    .back-arrow {
      display: none;
    }
  }

  &.light-mode-active {
    background-color: map-get($colours, white);

    .section {
      header {
        color: map-get($colours, black);
      }

      &.shortcuts {
        border-bottom: 0.1rem solid map-get($component-colours, border-dark);
      }

      .apps {
        button {
          color: map-get($colours, grey-7);

          &:hover:not(:disabled) {
            background-color: map-get($component-colours, black-overlay-5);
            filter: none;
          }

          &:focus-visible {
              box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
              outline: solid 0.2rem rgba(255, 233, 254, 1);
              border: solid 0.1rem map-get($colours, accent-purple3);
          }
        }
      }
    }
  }

  &.p-popover {
    &:after,
    &:before {
      display: none;
    }
  }

  .p-popover-content {
    padding: 0;
  }

  .section {
    padding: 2.4rem 0;

    header {
      @include section-header-2;
      color: map-get($colours, white);
      padding: 0 $outside-horizontal-padding-s 2.4rem;

      @include cwh($screen-width-breakpoint, $screen-height-breakpoint) {
        padding-left: $outside-horizontal-padding-l;
        padding-right: $outside-horizontal-padding-l;
      }
    }
  }

  .shortcuts {
    border-bottom: 0.1rem solid map-get($component-colours, border-light);

    header {
      display: flex;
      align-items: center;

      .back-arrow {
        margin-right: 1.6rem;
      }
    }

    .apps {
      display: grid;
      grid-template-columns: auto auto auto;
      gap: 2.4rem 1.6rem;
      padding: 0 $outside-horizontal-padding-s;
      justify-items: center;

      @include rwd(700) {
        grid-template-columns: auto auto auto auto;
      }

      @include ch($screen-height-breakpoint) {
        grid-template-columns: auto auto auto;
      }

      @include cwh($screen-width-breakpoint, $screen-height-breakpoint) {
        padding: 0 $outside-horizontal-padding-l;
      }

      button {
        background-color: transparent;
        @include caption;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0;
        padding-bottom: 0.4rem;
        color: map-get($colours, grey-2);
        white-space: pre-wrap;
        width: 8rem;
        border-radius: 0.8rem;

        &:focus-visible {
              box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
              outline: solid 0.2rem rgba(255, 233, 254, 1);
              border: solid 0.1rem map-get($colours, accent-purple3);
        }

        @include ch(750) {
          @include section-sub-header;
          width: 9.4rem;
        }

        @include ch(950) {
          @include section-header-3;
          width: 11.2rem;
        }

        &:disabled {
          color: map-get($colours, grey-5);
        }

        img {
          margin: $button-padding;
          width: $icon-size-s;
          height: $icon-size-s;

          @include ch(750) {
            width: $icon-size-m;
            height: $icon-size-m;
          }
        }

        span {
          margin: 0 0.4rem;
        }

        &:hover:not(:disabled) {
          background-color: map-get($component-colours, black-overlay-30);
          filter: none;
        }
      }
    }
  }

  .help-feedback {
    .links {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 0 $outside-horizontal-padding-s;

      @include cwh($screen-width-breakpoint, $screen-height-breakpoint) {
        padding: 0 $outside-horizontal-padding-l;
      }

      > *:not(:first-child) {
        margin-top: 2.4rem;
      }
    }
  }
  
  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: all 0.1s ease-out;
  }

  .slide-up-enter-from {
    opacity: 0;
    transform: translateX(20px);
  }

  .slide-up-leave-to {
    opacity: 0;
    transform: translateX(-20px);
  }
}
</style>